import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { focusInto, installFocusTrap } from './focusTrap';

/** 開いているダイアログ（後に開いたものが末尾）。キー操作とフォーカスは末尾のものだけが扱う。 */
const openDialogs: { element: () => HTMLElement | null }[] = [];

const isTop = (entry: { element: () => HTMLElement | null }) =>
  openDialogs[openDialogs.length - 1] === entry;

/**
 * 2026-10-24 作業18（Safari・VoiceOver）。モーダルのダイアログは body の直下に出す。
 *
 * 画面の中（Layout の main・article の中）に置くと、aria-modal で背景の中身は隠れても、ダイアログを
 * 包む main・article の枠が残り、Safari の VoiceOver が開いた直後に「カラのメイン」「クライアント分析
 * カラの記事」を読んだ。ダイアログの外枠（背景の幕を含む要素）をこれで包む。React の文脈・イベントは
 * 今までどおり親の部品から届く。
 */
export function inModalLayer(node: ReactNode) {
  return createPortal(node, document.body);
}

/** 開いている間、背景（body の直下のほかの要素）に付けた inert の印。付けたものだけを外す。 */
const INERT_MARK = 'data-modal-inert';

/** body の直下の、element を含む要素（body の外なら null）。 */
function layerOf(element: HTMLElement): Element | null {
  let node: Element = element;
  while (node.parentElement && node.parentElement !== document.body) node = node.parentElement;
  return node.parentElement === document.body ? node : null;
}

/**
 * 作業18。手前のダイアログの層だけを残し、body の直下のほかの要素（アプリの画面・下のダイアログ）に
 * inert を付ける（読み上げの移動・クリック・フォーカスが背景へ行かない）。ダイアログが無ければ外す。
 * もとから inert の要素は触らない。
 */
function syncBackgroundInert() {
  const top = openDialogs[openDialogs.length - 1]?.element();
  const keep = top && top.isConnected ? layerOf(top) : null;
  Array.from(document.body.children).forEach((child) => {
    const marked = child.hasAttribute(INERT_MARK);
    if (keep !== null && child !== keep) {
      if (!marked && !child.hasAttribute('inert')) {
        child.setAttribute('inert', '');
        child.setAttribute(INERT_MARK, '');
      }
    } else if (marked) {
      child.removeAttribute('inert');
      child.removeAttribute(INERT_MARK);
    }
  });
}

/**
 * ADM-16。モーダルのダイアログのキーボード操作の基準。
 *
 * - 開いたら、最初の入力欄（無ければ最初の操作）へフォーカスを移す。
 * - Tab・Shift+Tab はダイアログの中で巡回し、背景の画面へ抜けない（中の移動はブラウザ、端は
 *   番兵で折り返す。EV-123・EV-124）。
 * - Escape で閉じる（onClose）。重なったダイアログでは手前のものだけが閉じる。
 * - 閉じたら、開く前にフォーカスがあった操作（開いたボタンなど）へ戻す。
 *
 * EV-119。Escape とフォーカスの出入りは document で受ける（フォーカスが外にあっても効く）。
 * フォーカスが外へ出たら中へ戻す。開く前のフォーカスが BODY か、もう無いときは、
 * 下に残ったダイアログへ戻す。
 *
 * 返した ref を role="dialog"（aria-modal・aria-labelledby 付き）の要素に付け、
 * その要素には tabIndex={-1} を付ける（操作が無いときのフォーカス先）。
 * ダイアログの外枠は inModalLayer で body の直下に出す（作業18）。開いている間は背景に inert を付ける。
 */
export function useModalDialog<T extends HTMLElement = HTMLDivElement>(
  isOpen: boolean,
  onClose: () => void
) {
  // EV-117。要素が作り直されたとき（中のダイアログを開く間に外したなど）も処理を付け直すため、
  // 要素を state で持つ（callback ref）。閉じたときの戻し先の判断には最新の要素を ref で見る。
  const [container, setContainer] = useState<T | null>(null);
  const elementRef = useRef<T | null>(null);
  const ref = useCallback((element: T | null) => {
    elementRef.current = element;
    setContainer(element);
  }, []);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const entryRef = useRef<{ element: () => HTMLElement | null } | null>(null);

  // 開いている間の登録と、開く前のフォーカス（閉じたら戻す）。
  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    const entry = { element: () => elementRef.current };
    entryRef.current = entry;
    openDialogs.push(entry);
    return () => {
      const index = openDialogs.indexOf(entry);
      if (index >= 0) openDialogs.splice(index, 1);
      entryRef.current = null;
      // 戻し先が inert のままだと focus() が効かないので、先に外す（下のダイアログが残れば付け直す）。
      syncBackgroundInert();
      if (opener && opener !== document.body && opener.isConnected) {
        opener.focus();
        return;
      }
      // 戻し先が無ければ、下に残ったダイアログの中へ。
      const below = openDialogs[openDialogs.length - 1]?.element();
      if (below && below.isConnected) focusInto(below);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !container) return undefined;
    const entry = entryRef.current;
    const active = () => entry !== null && isTop(entry);

    // Tab の移動はブラウザに任せ、端の番兵・外へ出たときの戻し・Escape を付ける（focusTrap.ts）。
    const remove = installFocusTrap(container, {
      isActive: active,
      onEscape: () => onCloseRef.current(),
    });
    if (active() && !container.contains(document.activeElement)) focusInto(container);
    syncBackgroundInert();
    return () => {
      remove();
      // 要素が作り直されるときは、新しい要素で付け直す（上の effect）。
      syncBackgroundInert();
    };
  }, [isOpen, container]);

  return ref;
}
